import { КЕГЛЬ_ВЕЛИЧИНЫ } from '@/shared/ui/metric';
import { cn } from '@/utils/cn';
// Плитка показателя. Три состояния, и они выглядят ПО-РАЗНОМУ ПО ФОРМЕ, а не оттенком серого.
//
// Правило 7: пустое, нулевое и неизвестное — три разных факта. В системе про зарплату это не
// эстетика: по такой плитке финансовый директор решает, платить или нет.
//
//   значение    — крупная цифра, моноширинно, рядом вторая величина словами;
//   ноль        — цифра 0 на серой заливке и ОБЯЗАТЕЛЬНАЯ причина, почему ноль;
//   неизвестно  — на месте числа НЕ цифра, а короткая фраза «чем считается», ниже —
//                 чего не хватает. Ни прочерка, ни нуля, ни спиннера: спиннер здесь врал
//                 бы, что данные едут, а они не едут вовсе — источника ещё нет.
//
// ## Размер и место ссылки, 25.08.2026
//
// Плитка была высотой не меньше 160 точек с отступом 20 и числом в 32 точки, а ссылка
// «Посмотреть подробно» прижималась к нижнему краю через `mt-auto`. Четыре такие плитки
// занимали 430 точек — треть экрана — и ссылки в них вставали на разной высоте, потому что
// подписи под числами разной длины. Читалось это как четыре съехавшие карточки.
//
// Теперь высота определяется содержимым, число — 24 точки (столько же, сколько у любой другой
// величины в продукте), а переход стоит в шапке плитки справа: место у него одно и то же
// независимо от того, что в плитке.

import type { KpiOut } from '@/shared/api/endpoints/accountable';
import { RiArrowDownSLine, RiArrowRightSLine } from '@remixicon/react';
import { Link } from 'react-router';

import { formatHours } from '@/shared/hours/format';
import { formatRubles } from '@/shared/money/format';

export interface KpiTileProps {
  /** Куда ведёт плитка за подробностями. Без адреса плитка остаётся неподвижной. */
  href?: string;
  /**
   * Разбор РАСКРЫВАЕТСЯ НА МЕСТЕ, а не уводит.
   *
   * Появилось 21.09.2026. Плитка «Неподтверждённый подотчёт» вела на `/money/accountable` —
   * то есть на экран, где человек уже стоял. Нажатие не делало ничего видимого и вдобавок
   * молча сбрасывало наложенные отборы: единственным его следом была потеря уже сделанной
   * работы. Заказчик прочёл это как «ничего не происходит», и был прав.
   *
   * Чинится это не другим адресом, а отказом от адреса: если разрез живёт на ТОМ ЖЕ экране,
   * его место — под плиткой, а не за ссылкой. Взаимоисключающе с `href`.
   */
  onOpen?: () => void;
  /** Раскрыт ли разбор. Меняет только слово: «Подробно» против «Свернуть». */
  открыто?: boolean;
  title: string;
  value: KpiOut;
  /** Вторая величина под числом: «у 14 человек», «на 7 объектах». */
  footnote?: string;
}

export function KpiTile({ title, value, footnote, href, onOpen, открыто = false }: KpiTileProps) {
  // Показатель без разреза — тупик: человек видит число и не может спросить «из чего оно».
  // Разрез есть не у всех: у «неизвестно» его нет по определению, вести там некуда.
  const естьРазрез = href !== undefined || onOpen !== undefined;
  const ведёт = естьРазрез && value.state !== 'unknown';

  // Плитка объявлена группой со своим названием: это и её смысл для чтения с экрана («здесь
  // показатель такой-то»), и единственный устойчивый способ найти её целиком — по подписи, а
  // не по ближайшему родителю какого-нибудь текста внутри.
  // Нажимается ВСЯ плитка, а не только слова «Подробно». Ссылкой была одна строка в углу,
  // и на живом экране это выглядело так: человек тычет в крупное число, ничего не
  // происходит, он тычет ещё раз. Плитка на панели показателей — это карточка, и от карточки
  // ждут, что она откроется целиком; «Подробно ›» остаётся как признак, что открывать есть
  // куда, но перестаёт быть единственной мишенью в шестьдесят точек.
  const тело = (
    <>
      <div className='flex items-center justify-between gap-3'>
        <span className='truncate text-paragraph-xs text-text-sub-600'>{title}</span>
        {ведёт && (
          <span className='flex shrink-0 items-center gap-0.5 text-paragraph-xs text-primary-base'>
            {открыто ? 'Свернуть' : 'Подробно'}
            {onOpen !== undefined ? (
              // Стрелка ВНИЗ у раскрытия и вбок у перехода: это два разных обещания, и
              // человек читает их до нажатия. Стрелка вбок на месте, где ничего никуда не
              // уходит, — обещание, которое экран не выполнит.
              <RiArrowDownSLine
                className={cn('size-4 transition-transform', открыто && 'rotate-180')}
              />
            ) : (
              <RiArrowRightSLine className='size-4' />
            )}
          </span>
        )}
      </div>
      {value.state === 'unknown' ? <Неизвестно value={value} /> : null}
      {value.state === 'zero' ? <Ноль value={value} /> : null}
      {value.state === 'value' ? <Значение value={value} footnote={footnote} /> : null}
    </>
  );

  return (
    <div
      role='group'
      aria-label={title}
      className='rounded-xl border border-stroke-soft-200 bg-bg-white-0'
    >
      {ведёт && onOpen !== undefined ? (
        <button
          type='button'
          aria-expanded={открыто}
          onClick={onOpen}
          className='flex w-full flex-col gap-2 rounded-xl p-4 text-left transition-colors hover:bg-bg-weak-50'
        >
          {тело}
        </button>
      ) : ведёт && href !== undefined ? (
        <Link
          to={href}
          className='flex flex-col gap-2 rounded-xl p-4 transition-colors hover:bg-bg-weak-50'
        >
          {тело}
        </Link>
      ) : (
        <div className='flex flex-col gap-2 p-4'>{тело}</div>
      )}
    </div>
  );
}

function Значение({ value, footnote }: { value: KpiOut; footnote?: string }) {
  // Знак рубля ставится ЗДЕСЬ, а не на каждой плитке отдельно: плитка и так знает, деньги
  // перед ней или счёт — по тому, какое из двух полей пришло непустым. Без знака на панели
  // рядом вставали «14 348 222,00» (деньги), «5» (объекты) и «−1494» (часы), и отличить
  // денежное от счётного было не по чему (кадр `кабинет-подотчёт.png`, 03.09.2026).
  //
  // Счёт печатается по тем же правилам, что часы и деньги: разряды неразрывным пробелом, минус
  // типографский. До 23.09.2026 плитка писала «-1494», а сводка «Расчёта» то же число —
  // «−1 494»: одно число двумя видами на соседних экранах.
  const число =
    value.amount !== null ? formatRubles(value.amount) : formatHours(String(value.count ?? 0));
  return (
    <>
      <span className={cn(КЕГЛЬ_ВЕЛИЧИНЫ.важный, 'tabular-nums text-text-strong-950')}>
        {число}
      </span>
      {footnote !== undefined && (
        <span className='text-paragraph-xs text-text-sub-600'>{footnote}</span>
      )}
      {/* Оговорка к числу: «только заведённые в программе…». Сервер пишет её ровно тогда,
          когда число неполное, — иначе часть читается как целое. 23.09.2026 так читались
          долги: в плитке стояли заведённые в программе, а старых, не перенесённых, не было. */}
      {value.reason !== null && value.reason !== undefined && value.reason !== '' && (
        <span className='text-paragraph-xs text-text-sub-600'>{value.reason}</span>
      )}
    </>
  );
}

function Ноль({ value }: { value: KpiOut }) {
  // Ноль денег и ноль штук — разные нули. Форма у них общая и намеренно (серая заливка вместо
  // крупной цифры: правило 7 разводит «ноль по расчёту» и «значение» по форме), а единица
  // остаётся: «0 ₽» отвечает на вопрос «ноль чего», голый «0» его задаёт.
  const ноль = value.amount !== null ? formatRubles(0) : '0';
  return (
    <>
      <span
        className={cn(
          КЕГЛЬ_ВЕЛИЧИНЫ.важный,
          'w-fit rounded-md bg-bg-weak-50 px-2 tabular-nums text-text-sub-600',
        )}
      >
        {ноль}
      </span>
      {/* Ноль по расчёту доказывает, что считали. Без причины он неотличим от «не считали». */}
      <span className='text-paragraph-xs text-text-sub-600'>{value.reason}</span>
    </>
  );
}

function Неизвестно({ value }: { value: KpiOut }) {
  return (
    <>
      {/* На месте числа фраза, а не цифра и не прочерк: прочерк читается как «денег нет». */}
      <span className='text-label-md text-text-sub-600'>{value.reason}</span>
      <span className='text-paragraph-xs text-text-sub-600'>{value.expected_from}</span>
    </>
  );
}
